import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
// Removed FontAwesome icons - using simple text/HTML elements instead

const DateContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0;
  
  @media (max-width: 768px) {
    padding: 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 2rem;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 1.5rem;
  }
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: 1fr 340px;
  gap: 1rem;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const CalendarSection = styled.div`
  background: ${props => props.theme.colors.offWhite};
  border-radius: 10px;
  padding: 1rem;
  margin-bottom: 1rem;
  overflow: hidden; /* Prevent calendar from overflowing */
  
  @media (max-width: 768px) {
    padding: 0.75rem;
    margin-left: -0.5rem;
    margin-right: -0.5rem;
    border-radius: 8px;
  }
`;

const CalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  
  button {
    background: none;
    border: none;
    color: ${props => props.theme.colors.accent};
    font-size: 1.5rem;
    cursor: pointer;
    padding: 0.5rem;
    border-radius: 50%;
    transition: all 0.2s ease;
    min-width: 44px; /* Touch target size */
    min-height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover {
      background: ${props => props.theme.colors.accent};
      color: white;
    }
    
    @media (max-width: 768px) {
      font-size: 1.25rem;
      padding: 0.375rem;
    }
  }
  
  h3 {
    font-size: 1.5rem;
    color: ${props => props.theme.colors.primary};
    margin: 0;
    flex: 1;
    text-align: center;
    
    @media (max-width: 768px) {
      font-size: 1.1rem;
    }
  }
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.5rem;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    gap: 0.25rem;
  }
  
  @media (max-width: 480px) {
    gap: 0.2rem;
  }
`;

const WeekDay = styled.div`
  text-align: center;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  padding: 0.5rem;
  font-size: 0.9rem;
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
    padding: 0.375rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.7rem;
    padding: 0.25rem;
  }
`;

const Day = styled.button`
  aspect-ratio: 1;
  border: none;
  background: ${({ availability, isSelected, theme }) => {
    if (isSelected) return theme.colors.accent;
    switch (Number(availability)) {
      case 1: return 'white';    // Available
      case 2: return '#ffd700';  // Limited Availability
      case 3: return '#90ee90';  // Booking Duration
      case 4: return '#e0e0e0';  // Not Available
      default: return theme.colors.greyBackground;
    }
  }};
  color: ${({ isSelected, availability }) => 
    isSelected 
      ? 'white' 
      : Number(availability) === 4
        ? '#999' 
        : '#000'};
  border-radius: 8px;
  cursor: ${({ availability }) => Number(availability) === 1 ? 'pointer' : 'not-allowed'};
  transition: all 0.2s ease;
  font-size: 0.9rem;
  font-weight: 500;
  min-width: 44px; /* Touch target size */
  min-height: 44px;
  
  &:hover {
    transform: ${({ availability }) => Number(availability) === 1 ? 'scale(1.05)' : 'none'};
    background: ${({ availability, isSelected, theme }) => 
      !isSelected && Number(availability) === 1 ? theme.colors.offWhite : ''};
  }
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
    min-width: 38px;
    min-height: 38px;
    border-radius: 6px;
  }
  
  @media (max-width: 480px) {
    font-size: 0.75rem;
    min-width: 32px;
    min-height: 32px;
    border-radius: 4px;
  }
`;

const CalendarLegend = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
  justify-content: center;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 0.75rem;
    font-size: 0.85rem;
  }
  
  @media (max-width: 480px) {
    gap: 0.5rem;
  }
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  color: ${props => props.theme.colors.secondary};
  
  span {
    width: 1rem;
    height: 1rem;
    border-radius: 4px;
    background: ${({ color }) => color};
    flex-shrink: 0;
  }
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
    
    span {
      width: 0.875rem;
      height: 0.875rem;
    }
  }
  
  @media (max-width: 480px) {
    font-size: 0.7rem;
    
    span {
      width: 0.75rem;
      height: 0.75rem;
    }
  }
`;

const TimeSlotsSection = styled.div`
  position: sticky;
  top: 160px; /* Increased to account for nav bar height (120px) + extra spacing */
  height: fit-content;
  background: white;
  border-radius: 10px;
  padding: 1rem;
  border: 2px solid ${props => props.theme.colors.greyBackground};
  
  @media (max-width: 992px) {
    position: static; /* Disable sticky on mobile */
    margin-top: 1rem;
    padding: 1.5rem;
  }
  
  @media (max-width: 768px) {
    padding: 1.25rem;
  }
`;

const TimeSlotsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 0.75rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const TimeSlot = styled.button`
  padding: 1rem;
  border: 2px solid ${props => props.isSelected ? props.theme.colors.accent : props.theme.colors.greyBackground};
  background: ${props => props.isSelected ? props.theme.colors.accent : 'white'};
  color: ${props => props.isSelected ? 'white' : props.theme.colors.primary};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 1rem;
  font-weight: 500;
  min-height: 48px; /* Touch-friendly height */
  
  &:hover:not(:disabled) {
    border-color: ${props => props.theme.colors.accent};
    background: ${props => props.isSelected ? props.theme.colors.accent : props.theme.colors.offWhite};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    padding: 0.875rem;
    font-size: 0.95rem;
  }
`;

const SelectedDateInfo = styled.div`
  background: linear-gradient(135deg, ${props => props.theme.colors.accent}, ${props => props.theme.colors.primary});
  color: white;
  padding: 1.5rem;
  border-radius: 12px;
  margin-bottom: 2rem;
  text-align: center;
  
  @media (max-width: 768px) {
    padding: 1.25rem;
    margin-bottom: 1.5rem;
  }
`;

const SelectedDateText = styled.div`
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const SelectedTimeText = styled.div`
  font-size: 1rem;
  opacity: 0.9;
  
  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
`;

const InfoBox = styled.div`
  background: linear-gradient(135deg, #e3f2fd, #f3e5f5);
  border: 1px solid ${props => props.theme.colors.accent};
  border-radius: 8px;
  padding: 1.5rem;
  margin-top: 2rem;
  text-align: center;
  
  @media (max-width: 768px) {
    padding: 1.25rem;
    margin-top: 1.5rem;
  }
`;

const InfoText = styled.p`
  color: ${props => props.theme.colors.primary};
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.5;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const FLOWER_GARDEN_PAGE_ID = '1ced1e1a-b442-4dee-b1e1-ae8b5b7bee09';
const AFTERNOON_TEA_PAGE_ID = '8b64eaf5-431e-4a3e-9194-e243e5cbdbdc';

const DateSelection = ({ 
  availableDates, 
  selectedDate, 
  timeSlots, 
  selectedTimeSlot, 
  onDateSelect, 
  onTimeSlotSelect,
  pageId
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Function to find the next available date
  const findNextAvailableDate = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day
    
    // Convert availableDates object to array of dates and sort them
    const availableDateStrings = Object.keys(availableDates || {})
      .filter(dateString => {
        const date = new Date(dateString);
        date.setHours(0, 0, 0, 0);
        const availability = availableDates[dateString];
        // Only include future dates that are available (availability === 1)
        if (date < today || availability !== 1) {
          return false;
        }
        
        // For afternoon tea page, apply front-end restrictions
        if (pageId === AFTERNOON_TEA_PAGE_ID) {
          const currentYear = new Date().getFullYear();
          const march14 = new Date(currentYear, 2, 14); // Month is 0-indexed, so 2 = March
          march14.setHours(0, 0, 0, 0);
          
          // Exclude dates before March 14th
          if (date < march14) {
            return false;
          }
          
          // Exclude the 14th, 15th, and 16th (closed dates, opens on 19th)
          const dayOfMonth = date.getDate();
          if (dayOfMonth === 14 || dayOfMonth === 15 || dayOfMonth === 16) {
            return false;
          }
          
          // Exclude Tuesdays and Wednesdays
          const dayOfWeek = (date.getDay() + 6) % 7; // Monday = 0, Tuesday = 1, Wednesday = 2, etc.
          if (dayOfWeek === 1 || dayOfWeek === 2) {
            return false;
          }
        }
        
        return true;
      })
      .sort(); // Sort chronologically
    
    if (availableDateStrings.length > 0) {
      return new Date(availableDateStrings[0]);
    }
    
    // If no available dates found, for afternoon tea page, start from March 14th
    if (pageId === AFTERNOON_TEA_PAGE_ID) {
      const currentYear = new Date().getFullYear();
      const march14 = new Date(currentYear, 2, 14); // Month is 0-indexed, so 2 = March
      march14.setHours(0, 0, 0, 0);
      
      // If March 14th is in the past, use it anyway (will show next month)
      if (march14 < today) {
        return march14;
      }
      
      // Find the next available day after March 14th that's not Tuesday or Wednesday
      let checkDate = new Date(march14);
      for (let i = 0; i < 7; i++) {
        const dayOfWeek = (checkDate.getDay() + 6) % 7;
        if (dayOfWeek !== 1 && dayOfWeek !== 2) {
          return checkDate;
        }
        checkDate.setDate(checkDate.getDate() + 1);
      }
      
      return march14;
    }
    
    // If no available dates found, return current month
    return new Date();
  };

  // Update currentMonth when availableDates changes to show the month with next available date
  useEffect(() => {
    if (availableDates && Object.keys(availableDates).length > 0) {
      const nextAvailableDate = findNextAvailableDate();

      setCurrentMonth(nextAvailableDate);
    }
  }, [availableDates]);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const days = new Date(year, month + 1, 0).getDate();
    // Adjust firstDay so Monday is 0, Tuesday is 1, ..., Sunday is 6
    const firstDay = (new Date(year, month, 1).getDay() + 6) % 7;
    return { days, firstDay };
  };

  const handlePrevMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1));
  };

  const getDateAvailability = (date) => {
    // Format date in local timezone to avoid UTC conversion issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;
    const baseAvailability = availableDates[dateString] || 4; // Default to not available
    
    // For afternoon tea page, apply special restrictions
    if (pageId === AFTERNOON_TEA_PAGE_ID) {
      const currentYear = new Date().getFullYear();
      const march14 = new Date(currentYear, 2, 14); // Month is 0-indexed, so 2 = March
      march14.setHours(0, 0, 0, 0);
      
      // Disable all dates before March 14th
      if (date < march14) {
        return 4; // Not available
      }
      
      // Disable the 14th, 15th, and 16th (closed dates, opens on 19th)
      const dayOfMonth = date.getDate();
      if (dayOfMonth === 14 || dayOfMonth === 15 || dayOfMonth === 16) {
        return 4; // Not available
      }
      
      // Get day of week: Monday = 0, Tuesday = 1, Wednesday = 2, ..., Sunday = 6
      const dayOfWeek = (date.getDay() + 6) % 7;
      // Tuesday (1) and Wednesday (2) are not available for afternoon tea
      if (dayOfWeek === 1 || dayOfWeek === 2) {
        return 4; // Not available
      }
    }
    
    return baseAvailability;
  };

  const renderCalendar = () => {
    const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const { days, firstDay } = getDaysInMonth(currentMonth);
    const today = new Date();
    // Normalize today to start of day for proper comparison
    today.setHours(0, 0, 0, 0);

    return (
      <CalendarSection>
        <CalendarHeader>
          <button onClick={handlePrevMonth}>&lt;</button>
          <h3>
            {currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}
          </h3>
          <button onClick={handleNextMonth}>&gt;</button>
        </CalendarHeader>
        <CalendarGrid>
          {weekDays.map(day => (
            <WeekDay key={day}>{day}</WeekDay>
          ))}
          {[...Array(firstDay)].map((_, index) => (
            <Day key={`empty-${index}`} availability={0} />
          ))}
          {[...Array(days)].map((_, index) => {
            const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), index + 1);
            // Normalize date to start of day for proper comparison
            date.setHours(0, 0, 0, 0);
            const availability = getDateAvailability(date);
            const isSelected = selectedDate &&
              date.getDate() === selectedDate.getDate() &&
              date.getMonth() === selectedDate.getMonth() &&
              date.getFullYear() === selectedDate.getFullYear();
            // Check if date is in the past (before today, not including today)
            const isPast = date < today;
            
            // Check restrictions for afternoon tea page
            let isAfternoonTeaRestricted = false;
            let tooltipText = null;
            
            if (pageId === AFTERNOON_TEA_PAGE_ID) {
              const currentYear = new Date().getFullYear();
              const march14 = new Date(currentYear, 2, 14); // Month is 0-indexed, so 2 = March
              march14.setHours(0, 0, 0, 0);
              
              // Check if date is before March 14th
              if (date < march14) {
                isAfternoonTeaRestricted = true;
                tooltipText = 'Afternoon Tea is not available before March 14th';
              } else {
                // Check if this is the 14th, 15th, or 16th (closed dates, opens on 19th)
                const dayOfMonth = date.getDate();
                if (dayOfMonth === 14 || dayOfMonth === 15 || dayOfMonth === 16) {
                  isAfternoonTeaRestricted = true;
                  tooltipText = 'Not available on this date. Opens on the 19th.';
                } else {
                  // Check if this is a Tuesday or Wednesday
                  const dayOfWeek = (date.getDay() + 6) % 7; // Monday = 0, Tuesday = 1, Wednesday = 2, etc.
                  if (dayOfWeek === 1 || dayOfWeek === 2) {
                    isAfternoonTeaRestricted = true;
                    tooltipText = 'Afternoon Tea is not available on this date';
                  }
                }
              }
            }

            return (
              <Day
                key={index}
                availability={isPast ? 4 : availability}
                isSelected={isSelected}
                onClick={() => !isPast && availability === 1 && !isAfternoonTeaRestricted && onDateSelect(date)}
                disabled={isPast || availability !== 1 || isAfternoonTeaRestricted}
                title={tooltipText || undefined}
              >
                {index + 1}
              </Day>
            );
          })}
        </CalendarGrid>
        <CalendarLegend>
          <LegendItem color="#e0e0e0">
            <span></span>Not Available
          </LegendItem>
          <LegendItem color="#ffd700">
            <span></span>Limited Availability
          </LegendItem>
          <LegendItem color="#90ee90">
            <span></span>Booking Duration
          </LegendItem>
          <LegendItem color="white">
            <span></span>Available
          </LegendItem>
        </CalendarLegend>
      </CalendarSection>
    );
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-GB', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeSlot) => {
    return timeSlot.time || timeSlot;
  };

  // Helper function to check if a time slot is selected
  const isTimeSlotSelected = (timeSlot) => {
    if (!selectedTimeSlot) return false;
    
    // Compare by time string if both have time property
    if (timeSlot.time && selectedTimeSlot.time) {
      return timeSlot.time === selectedTimeSlot.time;
    }
    
    // Compare by eventId if available
    if (timeSlot.eventId && selectedTimeSlot.eventId) {
      return timeSlot.eventId === selectedTimeSlot.eventId;
    }
    
    // Fallback to object equality
    return selectedTimeSlot === timeSlot;
  };

  return (
    <DateContainer>
      <SectionTitle>
        Select Your Visit Date
      </SectionTitle>

      <Layout>
        <div>
          {renderCalendar()}

          {selectedDate && (
            <SelectedDateInfo>
              <SelectedDateText>
                Selected Date: {formatDate(selectedDate)}
              </SelectedDateText>
              {selectedTimeSlot && (
                <SelectedTimeText>
                  Selected Time: {formatTime(selectedTimeSlot)}
                </SelectedTimeText>
              )}
              {selectedDate && !selectedTimeSlot && timeSlots.length > 0 && (
                <SelectedTimeText>
                  Please select a time slot below
                </SelectedTimeText>
              )}
            </SelectedDateInfo>
          )}

          <InfoBox>
            <InfoText>
              Select your preferred visit date from the calendar above. Available dates are shown in white.
              {timeSlots.length > 0 && ' Then choose a time slot from the right panel.'}
              {pageId === AFTERNOON_TEA_PAGE_ID && (
                <div style={{ marginTop: '0.75rem', fontWeight: 600, color: '#d32f2f' }}>
                  Note: Afternoon Tea is not available before March 14th, and is not available on Tuesdays and Wednesdays.
                </div>
              )}
            </InfoText>
          </InfoBox>
        </div>

        {selectedDate && (
          <TimeSlotsSection>
            <h3>
              Available Time Slots
            </h3>
            {pageId === FLOWER_GARDEN_PAGE_ID ? (
              <div style={{ lineHeight: 1.5 }}>
                This is an all-day event. A time will be applied automatically.
                {selectedTimeSlot?.time && (
                  <div style={{ marginTop: '0.5rem', fontWeight: 600 }}>
                    Selected Time: {formatTime(selectedTimeSlot)}
                  </div>
                )}
              </div>
            ) : timeSlots.length > 0 ? (
              <TimeSlotsGrid>
                {timeSlots.map((timeSlot, index) => {
                  const isSelected = isTimeSlotSelected(timeSlot);
                  return (
                    <TimeSlot
                      key={index}
                      isSelected={isSelected}
                      onClick={() => onTimeSlotSelect(timeSlot)}
                    >
                      {isSelected && '✓ '}
                      {formatTime(timeSlot)}
                    </TimeSlot>
                  );
                })}
              </TimeSlotsGrid>
            ) : (
              <div>No specific timeslots required for this date.</div>
            )}
          </TimeSlotsSection>
        )}
      </Layout>
    </DateContainer>
  );
};

export default DateSelection;